/**
 * CollectionGroupDetailPage — 컬렉션 묶음 상세 (IA SC-014 · SC-015).
 *
 * design `CollectionDetailPage` 의 구성을 실 API 로 옮긴 것이다.
 *   공통    헤더(묶음명) · 총 가치 · 검색 · ⋯ 관리 메뉴
 *   빈      안내 + '카드 추가'
 *   채워짐  카드별 대표 시세 · 정렬 · (가치 추이는 '데이터 준비 중')
 *
 * ⚠ **여기 금액은 내 보유 평가액이 아니다.** 묶음은 카드를 가리키지 보유 건을 가리키지
 *   않는다(수동 추가는 보유가 되지 않는다 — IA SC-016). 그래서 서버가 주는 값은 그 카드의
 *   **일반판 대표 시세**이고, 화면도 '카드 시세 합계' 라고 말한다. 보유 기준 평가액은
 *   컬렉션 탭의 총액이 답한다.
 * ⚠ 합계는 **시세를 아는 카드만** 더한다. 모르는 카드를 0 으로 세면 총액이 낮게 나와
 *   거짓말이 되므로, 몇 장으로 낸 합인지를 함께 밝힌다.
 *
 * ⚠ 가치 추이 차트는 자리만 둔다. 컬렉션 가치의 일자별 스냅샷이 서버에 없다 — 카드 상세의
 *   가격 이력과 같은 이유이고, 없는 데이터를 그리지 않는 것이 §5.4 규칙이다.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@astryxdesign/core/Button";
import { Card } from "@astryxdesign/core/Card";
import { EmptyState } from "@astryxdesign/core/EmptyState";
import { Heading } from "@astryxdesign/core/Heading";
import { IconButton } from "@astryxdesign/core/IconButton";
import { Section } from "@astryxdesign/core/Section";
import { Selector } from "@astryxdesign/core/Selector";
import { Skeleton } from "@astryxdesign/core/Skeleton";
import { Text } from "@astryxdesign/core/Text";
import { TextInput } from "@astryxdesign/core/TextInput";
import { VStack, HStack } from "@astryxdesign/core/Layout";
import CardThumb from "../../components/card/CardThumb";
import { collectionGroupName } from "../../components/collection/collectionGroupName";
import { cardImageUrl } from "../../api/card";
import Icon from "../../components/primitives/Icon";
import ConfirmDialog from "../../components/feedback/ConfirmDialog";
import { useToast } from "../../components/feedback/Toast";
import { useMoney } from "../../hooks/useMoney";
import { useOverlayNavigate } from "../../hooks/useOverlayNavigate";
import { useActivityNavigator } from "../../navigation/useActivityNavigator";
import {
  fetchCollectionGroup,
  removeCardFromGroup,
  type CollectionGroupCard,
} from "../../api/collectionGroup";
import { COLLECTION_GROUP_KEY } from "../../hooks/useCollectionGroupQueries";
import CollectionGroupManageSheet from "../../components/collection/CollectionGroupManageSheet";
import styles from "./CollectionGroupDetailPage.module.css";

/* 정렬 축 — 이름 / 평가금액 / 30일 등락 (design COLLECTION_SORTS).
   ⚠ '최근 담은 순' 은 없다. 구매가순·구매일순이 빠진 자리에 디자인이 넣은 것은
     30일 등락이고, 서버가 주는 최근 담은 순은 **미리보기 썸네일**의 축이지 이 목록의
     축이 아니다.
   ⚠ 이름공간이 `detailSort` 인 이유: 보유 카드(SC-002)의 정렬과 한때 `collection.sort.*`
     를 같이 썼는데, 보유 쪽에서 'name' 을 'set' 으로 바꾸자 이 화면의 '이름 순' 이
     통째로 사라졌다(design 주석). 목록이 다르면 이름공간도 달라야 한다. */
type GroupSort = "name" | "value" | "trend";

export default function CollectionGroupDetailPage({ groupSerialNo }: { groupSerialNo: number }) {
  const { t } = useTranslation("card");
  const toast = useToast();
  const money = useMoney();
  const queryClient = useQueryClient();
  const overlayNavigate = useOverlayNavigate();
  const activityNav = useActivityNavigator();

  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<GroupSort>("value");
  const [manageOpen, setManageOpen] = useState(false);
  /** 대량 편집 — 켜면 행마다 '빼기' 가 열린다 (SC-015 대량 편집기) */
  const [bulkEdit, setBulkEdit] = useState(false);
  const [removing, setRemoving] = useState<CollectionGroupCard | null>(null);

  const detail = useQuery({
    queryKey: [...COLLECTION_GROUP_KEY, groupSerialNo],
    queryFn: () => fetchCollectionGroup(groupSerialNo),
    staleTime: 30_000,
  });

  /** SC-014 검색 + 정렬 — 이름·번호로 좁히고 고른 축으로 정렬한다. */
  const rows = useMemo(() => {
    const all = detail.data?.cards ?? [];
    const q = query.trim().toLowerCase();
    const filtered = q
      ? all.filter(
          (r) =>
            r.card.name.toLowerCase().includes(q) ||
            (r.card.number ?? "").toLowerCase().includes(q),
        )
      : all;
    // ⚠ 값이 없는 행(시세·등락 미상)은 **항상 뒤로** 민다 — 0 으로 섞으면 순서가
    //   거짓말이 된다(design sortCollectionRows 와 같은 규칙).
    const last = (v: number | null | undefined) => (v === null || v === undefined ? -Infinity : v);
    return [...filtered].sort((a, b) => {
      if (sort === "name") return a.card.name.localeCompare(b.card.name, "ko");
      if (sort === "trend") return last(b.currentPrice?.trendRate) - last(a.currentPrice?.trendRate);
      return last(b.currentPrice?.amount) - last(a.currentPrice?.amount);
    });
  }, [detail.data?.cards, query, sort]);

  const removeCard = async () => {
    if (!removing) return;
    try {
      await removeCardFromGroup(groupSerialNo, removing.card.cardSerialNo);
      await queryClient.invalidateQueries({ queryKey: COLLECTION_GROUP_KEY });
      toast.success(t("collection.groupCardRemoved", { name: removing.card.name }));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("collection.groupFailed"));
    } finally {
      setRemoving(null);
    }
  };

  if (detail.isLoading) {
    return (
      <Section variant="transparent" padding={4} maxWidth={1120} width="100%" minHeight="100%">
        <VStack gap={4}>
          <Skeleton width="60%" height={28} />
          <Skeleton width="100%" height={120} />
        </VStack>
      </Section>
    );
  }

  if (detail.isError || !detail.data) {
    return (
      <Section variant="transparent" padding={4} maxWidth={1120} width="100%" minHeight="100%">
        <EmptyState
          title={t("collection.groupNotFound")}
          description={t("collection.groupNotFoundBody")}
          actions={
            <Button
              variant="primary"
              label={t("collection.backToList")}
              clickAction={() => overlayNavigate("/collections")}
            />
          }
        />
      </Section>
    );
  }

  const data = detail.data;
  const isEmpty = data.cards.length === 0;

  return (
    <Section variant="transparent" padding={4} maxWidth={1120} width="100%" minHeight="100%">
      <VStack gap={6}>
        {/* ── 헤더: 묶음명 + ⋯ 관리 ─────────────────────────────
            ⚠ 톱니가 아니라 **연필**이다 (design 2026-08-28). 톱니는 '앱 설정' 의 그림이라
              내 정보의 설정과 같은 말을 한다. 여기서 여는 것은 이 묶음을 손보는 메뉴다. */}
        <HStack gap={2} justify="between" align="center" wrap="wrap">
          <Heading level={2}>{collectionGroupName(data, t)}</Heading>
          <IconButton
            label={t("collection.manage")}
            variant="ghost"
            icon={<Icon name="Edit" size="md" />}
            clickAction={() => setManageOpen(true)}
          />
        </HStack>

        {/* 총 가치 — '카드 시세 합계' 다. 보유 평가액과 다르다(위 주석). */}
        <Card padding={4}>
          <VStack gap={1}>
            <Text type="supporting" color="secondary">{t("collection.groupTotalLabel")}</Text>
            <Heading level={2}>{money.format(data.totalValue ?? 0)}</Heading>
            <Text type="supporting" color="secondary">
              {t("collection.groupValuedBasis", {
                valued: data.valuedCount,
                total: data.cardCount,
              })}
            </Text>
          </VStack>
        </Card>

        {/* 가치 추이 — 데이터가 없다. 자리만 둔다(§5.4 없는 값을 만들지 않는다). */}
        <Card padding={4}>
          <VStack gap={1}>
            <HStack gap={2} align="center">
              <Text weight="semibold">{t("collection.groupTrendTitle")}</Text>
              <Text type="supporting" color="secondary">{t("detail.pendingBadge")}</Text>
            </HStack>
            <Text type="supporting" color="secondary">{t("collection.groupTrendPending")}</Text>
          </VStack>
        </Card>

        {isEmpty ? (
          <EmptyState
            title={t("collection.groupEmptyTitle")}
            description={t("collection.groupEmptyBody")}
            actions={
              <Button
                variant="primary"
                label={t("collection.action.addManual")}
                clickAction={() => {
                  activityNav.open({ url: `/collections/groups/${groupSerialNo}/add` });
                }}
              />
            }
          />
        ) : (
          <VStack gap={3}>
            <HStack gap={2} align="center" justify="between" wrap="wrap">
              <div className={styles.searchField}>
                <TextInput
                  label={t("collection.searchCards")}
                  isLabelHidden
                  value={query}
                  onChange={(v) => setQuery(v)}
                  placeholder={t("collection.searchCards")}
                  hasClear
                />
              </div>
              <Selector
                label={t("collection.sortLabel")}
                isLabelHidden
                options={[
                  { value: "name", label: t("collection.detailSort.name") },
                  { value: "value", label: t("collection.detailSort.value") },
                  { value: "trend", label: t("collection.detailSort.trend") },
                ]}
                value={sort}
                onChange={(v) => setSort(v as GroupSort)}
              />
            </HStack>

            {rows.length === 0 ? (
              <Text type="supporting" color="secondary">{t("search.empty")}</Text>
            ) : (
              <VStack gap={2}>
                {rows.map((row) => (
                  <Card key={row.mappingSerialNo} padding={3}>
                    <HStack gap={3} align="center" justify="between" width="100%">
                      <button
                        type="button"
                        className={styles.cardRow}
                        onClick={() => overlayNavigate(`/cards/${row.card.cardSerialNo}`)}
                      >
                        <HStack gap={3} align="center">
                          <CardThumb imgUrl={cardImageUrl(row.card.imagePathway) ?? undefined} />
                          <VStack gap={0.5} align="start">
                            <Text weight="semibold">{row.card.name}</Text>
                            <Text type="supporting" color="secondary">
                              {[row.card.cardsetCode?.toUpperCase(), row.card.number]
                                .filter(Boolean)
                                .join(" · ")}
                            </Text>
                            {/* 보유하지 않은 카드가 정상적으로 섞인다(수동 추가) — 표시로 가른다 */}
                            {!row.owned && (
                              <Text type="supporting" color="secondary">
                                {t("collection.groupNotOwned")}
                              </Text>
                            )}
                          </VStack>
                        </HStack>
                      </button>
                      <HStack gap={2} align="center">
                        <Text weight="bold">
                          {row.currentPrice?.amount == null
                            ? t("collection.noPrice")
                            : money.format(
                                row.currentPrice.amount,
                                row.currentPrice.currency ?? undefined,
                              )}
                        </Text>
                        {bulkEdit && (
                          <Button
                            variant="ghost"
                            size="sm"
                            label={t("collection.groupRemoveCard")}
                            clickAction={() => setRemoving(row)}
                          />
                        )}
                      </HStack>
                    </HStack>
                  </Card>
                ))}
              </VStack>
            )}
          </VStack>
        )}
      </VStack>

      <CollectionGroupManageSheet
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        groupSerialNo={groupSerialNo}
        groupName={collectionGroupName(data, t)}
        isDefault={data.defaultGroup}
        cards={data.cards}
        bulkEdit={bulkEdit}
        onToggleBulkEdit={() => setBulkEdit((v) => !v)}
      />

      <ConfirmDialog
        open={removing !== null}
        onClose={() => setRemoving(null)}
        title={t("collection.groupRemoveCardTitle")}
        description={t("collection.groupRemoveCardBody", { name: removing?.card.name ?? "" })}
        secondaryText={t("action.cancel", { ns: "common" })}
        primaryText={t("collection.groupRemoveCard")}
        onSecondaryPressed={() => setRemoving(null)}
        onPrimaryPressed={() => void removeCard()}
      />
    </Section>
  );
}
